import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { select, Store } from '@ngrx/store';
import moment from 'moment';
import { ReplaySubject } from 'rxjs';
import { filter, take, takeWhile, tap, withLatestFrom } from 'rxjs/operators';
import { IStatusMessage, SelectItem } from '../../../shared';
import { DisplayErrors, IShowErrors } from '../../../shared/errors-sidenav';
import { LoggingService } from '../../../shared/services/logging';
import * as fromUserInfo from '../../../user-info';
import * as fromStore from '../../store';
@Component({
    selector: 'ef-indirect-rate-management-presenter',
    templateUrl: './indirect-rate-management-presenter.component.html',
    styleUrls: ['./indirect-rate-management-presenter.component.scss'],
    standalone: false
})
export class IndirectRateManagementPresenterComponent
    extends DisplayErrors<fromStore.Structure.IndirectRateStructure>
    implements OnInit, OnDestroy, OnChanges
{
    @Input() FiscalYears: SelectItem[];
    @Input() CurrentFiscalYear: string;
    @Input() selectedErrorWithStatus: IStatusMessage;
    @Input() userInfo: fromUserInfo.IUserInfo;
    @Output() ShowError: EventEmitter<IShowErrors> =
        new EventEmitter<IShowErrors>();
    @Output() FiscalYearSelected = new EventEmitter<string>();
    @Output() Cancel = new EventEmitter();
    @Output() SetProcessingText = new ReplaySubject<any>();

    ShowAdjustments = new EventEmitter<boolean>();
    public RefreshSubject = new ReplaySubject();
    userInfoSubject = new ReplaySubject<fromUserInfo.IUserInfo>(1);
    indirectManagementGroup: UntypedFormGroup;
    showReview = false;
    numCols = 9;
    private active = true;
    enableFYImpactButton = false;

    fyMessageDisplayed = true;
    // fyInfoMessage1 = `The FIRST time a period is processed in eFAACT, the transactions are burdened using the following current calendar year indirect rates.`;
    // fyInfoMessage2 = `Subsequent period re-processing recognizes the "Reuse Indirect Rates" checkbox.`;
    // fyInfoMessage3 = `To ensure proper rates are used during initial processing of a period, read the following note before entering new indirect rates.`;
    fyInfoMessage = `The initial processing for a period calculates using the current calendar year indirect rates (shown below). If periods are re-processed, the Period Processing "Reuse Indirect Rates" checkbox controls whether the previous rates (checked) or current rates (unchecked) are used.`;
    fyImportantNote = `IMPORTANT: Complete the initial period processing for all periods in the fiscal year BEFORE entering indirect rates for a new fiscal year.`;

    constructor(
        private log: LoggingService,
        public store: Store<fromStore.Structure.State>
    ) {
        super();
        this.indirectManagementGroup = new UntypedFormGroup({
            ShowAdjustments: new UntypedFormControl(false),
            FiscalYear: new UntypedFormControl(null, Validators.required)
        });
        this.indirectManagementGroup.controls['FiscalYear'].valueChanges
            .pipe(
                takeWhile(() => this.active),
                tap(fy =>
                    this.log.verbose({
                        type: 'IndirectRateManagementPresenterComponent:FiscalYear:valueChanges',
                        fy
                    })
                )
            )
            .subscribe(fy => {
                this.FiscalYearSelected.emit(fy);
                this.enableFYImpactButton = false;
            });
    }
    ngOnInit(): void {
        this.ShowAdjustments.pipe(
            takeWhile(() => this.active),
            withLatestFrom(this.userInfoSubject),
            // filter(([value]) => !value),
            tap(([value, userInfo]) =>
                this.log.verbose({
                    type: 'IndirectRateManagementPresenterComponent:showAdjustments',
                    value,
                    userInfo
                })
            )
        ).subscribe(([value, userInfo]) => {
            this.indirectManagementGroup.controls['FiscalYear'].setValue(
                value ? moment(userInfo.FYEndDate).format('MM/DD/YYYY') : null
            );
            if (value) {
                this.indirectManagementGroup.controls[
                    'FiscalYear'
                ].setValidators([Validators.required]);
            } else {
                this.indirectManagementGroup.controls[
                    'FiscalYear'
                ].clearValidators();
            }
            this.indirectManagementGroup.controls[
                'FiscalYear'
            ].updateValueAndValidity();
            this.indirectManagementGroup.updateValueAndValidity();
        });
        this.Cancel.pipe(takeWhile(() => this.active)).subscribe(() => {
            this.enableFYImpactButton = false;
        });

        this.store
            .pipe(
                select(fromStore.Structure.selectAll),
                filter(x => !!x && x.length > 0),
                tap(x =>
                    this.log.verbose({
                        type: 'IndirectRateManagementPresenterComponent:IndirectRateStructures$:tap',
                        x
                    })
                ),
                // filter(rates => rates.some(rate => !!rate.FYenddate)),
                take(1)
            )
            .subscribe(rates => {
                if (rates.some(rate => !!rate.FYenddate)) {
                    // TODO: select adjust full fiscal year
                    // TODO: select year from the rate
                    const rate = rates.find(x => !!x.FYenddate);
                    this.log.verbose({
                        type: 'IndirectRateManagementPresenterComponent:IndirectRateStructures$',
                        rates
                    });
                    // this.onFiscalYearSelected(rate?.FYenddate);
                    this.ShowAdjustments.next(true);
                    this.indirectManagementGroup.controls[
                        'ShowAdjustments'
                    ].setValue(true);
                    this.indirectManagementGroup.controls[
                        'FiscalYear'
                    ].patchValue(moment(rate?.FYenddate).format('L'));
                }
            });

        this.indirectManagementGroup.controls['FiscalYear'].valueChanges
        .pipe(takeWhile(_ => this.active))
        .subscribe(value => {
            if (value != null) {
                this.fyMessageDisplayed = false;
            } else {
                this.fyMessageDisplayed = true;
            }

        });
    }
    ngOnDestroy(): void {
        this.active = false;
    }
    ngOnChanges(changes: SimpleChanges) {
        if (changes.userInfo && changes.userInfo.currentValue) {
            this.userInfoSubject.next(changes.userInfo.currentValue);
        }
    }
    onShowReview(show: boolean) {
        this.log.verbose({
            type: 'IndirectRateManagementPresenterComponent:onShowReview',
            show
        });
        this.showReview = show;
        this.RefreshSubject.next(show);
    }

    onSaveRateStructure($event: fromStore.Structure.IndirectRateStructure[]) {
        this.enableFYImpactButton = true;
    }

    onSetProcessingText($event) {
        this.SetProcessingText.next($event);
    }
}
